import api from './api';
import { mockData, useMockData, mainScreenQuestion, questionData, q1ChoicesData, q2ChoicesData, q3ChoicesData } from './mockData';
import { transformQuestionData, transformPPRPatterns } from './dataTransform';
import logger from '../utils/logger';

// Helper to simulate API response structure
const mockResponse = (data) => Promise.resolve({ data });

// Cache for transformed question data (per question)
let questionDataCache = {};

/**
 * Build query params with optional field selection.
 * Like Facebook's Graph API: ?fields=id,name,email
 *
 * @param {string[]} fields - Array of fields to include in response
 * @returns {string} Query string (e.g., "?fields=id,title,layers")
 *
 * Benefits:
 * - Smaller payloads = faster responses
 * - Reduced bandwidth usage
 * - Get exactly what you need
 */
const buildFieldsParam = (fields) => {
  if (!fields || fields.length === 0) return '';
  return `?fields=${fields.join(',')}`;
};

// Clear the cache (useful when data is updated in Django Admin)
export const clearQuestionCache = (questionId = null) => {
  if (questionId) {
    delete questionDataCache[questionId];
  } else {
    questionDataCache = {};
  }
  logger.log('Question cache cleared:', questionId || 'all');
};

/**
 * Batch service - fetches ALL question data in a single request
 * This significantly improves load times by reducing 8 API calls to 1
 *
 * Performance optimizations:
 * - Backend Redis caching (30 min TTL)
 * - Field selection support (?fields=id,title)
 * - Client-side caching
 */
export const batchService = {
  /**
   * Get all questionnaire data for a question in one request
   * @param {string} questionId - The question ID (e.g., 'Q10A')
   * @param {Object} options - Optional configuration
   * @param {string[]} options.fields - Specific fields to return (e.g., ['id', 'title', 'layers'])
   * @param {boolean} options.skipCache - Skip client-side cache
   * @returns {Promise} Response with question data and PPR patterns
   *
   * Example with field selection:
   *   batchService.getAll('Q10A', { fields: ['id', 'title', 'layers'] })
   */
  getAll: async (questionId = 'Q10A', options = {}) => {
    const { fields = [], skipCache = false } = options;

    if (useMockData) {
      // Return combined mock data
      return mockResponse({
        mainScreenQuestion,
        questionData,
        choicesData: { q1: q1ChoicesData, q2: q2ChoicesData, q3: q3ChoicesData },
        pprPatterns: []
      });
    }

    // Check cache first (unless skipped or using field selection)
    const cacheKey = fields.length > 0 ? `${questionId}_${fields.join(',')}` : questionId;
    if (!skipCache && questionDataCache[cacheKey]?.complete) {
      return { data: questionDataCache[cacheKey] };
    }

    try {
      // Fetch from batch endpoint with optional field selection
      const fieldsParam = buildFieldsParam(fields);
      const response = await api.get(`/v1/content/questions/${questionId}/batch/${fieldsParam}`);

      // Transform the data
      const transformed = transformQuestionData(response.question);
      const pprPatterns = transformPPRPatterns(response.ppr_patterns);

      // Build complete data object
      const completeData = {
        ...transformed,
        pprPatterns,
        complete: true  // Mark as complete batch fetch
      };

      // Cache the complete data
      questionDataCache[cacheKey] = completeData;

      return { data: completeData };
    } catch (error) {
      logger.warn('Batch endpoint failed, falling back to individual requests:', error.message);
      // Return null to signal fallback needed
      return { data: null, error };
    }
  },

  /**
   * Get only specific fields from a question (lightweight fetch)
   * Useful when you only need a subset of data (e.g., just title for breadcrumbs)
   *
   * @param {string} questionId - The question ID
   * @param {string[]} fields - Fields to fetch
   * @returns {Promise} Response with only requested fields
   */
  getFields: async (questionId, fields) => {
    if (!fields || fields.length === 0) {
      throw new Error('Fields array is required for getFields');
    }
    return batchService.getAll(questionId, { fields, skipCache: true });
  }
};

// Main question service - fetches the opening screen prompt
export const mainQuestionService = {
  get: async (questionId = 'Q10A') => {
    if (useMockData) {
      return mockResponse(mainScreenQuestion);
    }
    // Fetch question data and return main screen question
    const response = await api.get(`/v1/content/questions/${questionId}/`);
    const transformed = transformQuestionData(response);
    questionDataCache[questionId] = transformed;
    return { data: transformed.mainScreenQuestion };
  }
};

// Questions/Checkpoints service - fetches checkpoint metadata (q1, q2, q3)
export const questionsService = {
  getAll: async (params = {}) => {
    if (useMockData) {
      let questions = [...mockData.questions];
      if (params.section) questions = questions.filter(q => q.section === parseInt(params.section));
      if (params.type) questions = questions.filter(q => q.type === params.type);
      return mockResponse(questions);
    }
    // Fetch from Django and transform
    const response = await api.get('/v1/content/questions/');
    return { data: response };
  },
  getById: async (id) => {
    if (useMockData) {
      const question = mockData.questions.find(q => q.id === id);
      return mockResponse(question);
    }
    const response = await api.get(`/v1/content/questions/${id}/`);
    return { data: response };
  },
  getBySection: async (sectionId) => {
    if (useMockData) {
      const questions = mockData.questions.filter(q => q.section === parseInt(sectionId));
      return mockResponse(questions);
    }
    const response = await api.get(`/v1/content/questions/`);
    return { data: response };
  },
  // Get question metadata for specific checkpoint
  getQuestionData: async (questionId, checkpointKey) => {
    if (useMockData) {
      return mockResponse(questionData[checkpointKey]);
    }
    // Use cached data or fetch
    if (!questionDataCache[questionId]) {
      const response = await api.get(`/v1/content/questions/${questionId}/`);
      questionDataCache[questionId] = transformQuestionData(response);
    }
    return { data: questionDataCache[questionId].questionData[checkpointKey] };
  }
};

// Choices service - fetches choices for each checkpoint (q1, q2, q3)
export const choicesService = {
  getByCheckpoint: async (questionId, checkpointId) => {
    if (useMockData) {
      const checkpoint = mockData.checkpoints?.find(cp => cp.id === checkpointId);
      return mockResponse(checkpoint?.choices || []);
    }
    // Use cached data or fetch
    if (!questionDataCache[questionId]) {
      const response = await api.get(`/v1/content/questions/${questionId}/`);
      questionDataCache[questionId] = transformQuestionData(response);
    }
    return { data: questionDataCache[questionId].choicesData[checkpointId] || [] };
  },
  getQ1: async (questionId = 'Q10A') => {
    if (useMockData) {
      return mockResponse(q1ChoicesData);
    }
    // Use cached data or fetch
    if (!questionDataCache[questionId]) {
      const response = await api.get(`/v1/content/questions/${questionId}/`);
      questionDataCache[questionId] = transformQuestionData(response);
    }
    return { data: questionDataCache[questionId].choicesData.q1 || [] };
  },
  getQ2: async (questionId = 'Q10A') => {
    if (useMockData) {
      return mockResponse(q2ChoicesData);
    }
    // Use cached data or fetch
    if (!questionDataCache[questionId]) {
      const response = await api.get(`/v1/content/questions/${questionId}/`);
      questionDataCache[questionId] = transformQuestionData(response);
    }
    return { data: questionDataCache[questionId].choicesData.q2 || [] };
  },
  getQ3: async (questionId = 'Q10A') => {
    if (useMockData) {
      return mockResponse(q3ChoicesData);
    }
    // Use cached data or fetch
    if (!questionDataCache[questionId]) {
      const response = await api.get(`/v1/content/questions/${questionId}/`);
      questionDataCache[questionId] = transformQuestionData(response);
    }
    return { data: questionDataCache[questionId].choicesData.q3 || [] };
  }
};

// Checkpoints service - alias for questions in Django backend context
export const checkpointsService = {
  getAll: async (params = {}) => {
    if (useMockData) {
      return mockResponse(mockData.checkpoints);
    }
    const response = await api.get('/checkpoints/', { params });
    return { data: response };
  },
  getById: async (id) => {
    if (useMockData) {
      const checkpoint = mockData.checkpoints.find(cp => cp.id === id);
      return mockResponse(checkpoint);
    }
    const response = await api.get(`/checkpoints/${id}/`);
    return { data: response };
  }
};

// PPR service - fetches Personal Pattern Recognition data
export const pprService = {
  getByQuestion: async (questionId) => {
    if (useMockData) {
      return mockResponse([]);
    }
    const response = await api.get(`/v1/content/questions/${questionId}/ppr-patterns/`);
    return { data: transformPPRPatterns(response) };
  },
  /**
   * Match PPR pattern using backend anchor-based matching algorithm
   * @param {string} questionId - Question ID (e.g., 'Q10A')
   * @param {Object} selections - User selections
   * @param {number} selections.cp1 - CP1 option number (Layer 1 selection)
   * @param {Array<number>} selections.cp2 - CP2 option numbers (Layer 2 selections)
   * @param {number} selections.cp3 - CP3 option number (Layer 3 selection)
   * @returns {Promise} Response with matched pattern or null
   */
  match: async (questionId, selections) => {
    if (useMockData) {
      // Return null in mock mode - caller should use local fallback
      return mockResponse(null);
    }
    try {
      const response = await api.post(`/v1/content/questions/${questionId}/ppr/match/`, {
        cp1_selection: selections.cp1,
        cp2_selections: selections.cp2,
        cp3_selection: selections.cp3
      });
      return { data: response };
    } catch (error) {
      logger.warn('PPR match API failed:', error.message);
      return { data: null, error };
    }
  }
};

// Responses service - manages user answer submissions
export const responsesService = {
  getAll: async (params = {}) => {
    if (useMockData) {
      return mockResponse([]);
    }
    const response = await api.get('/v1/user/responses/', { params });
    return { data: response };
  },
  getByQuestion: async (questionId) => {
    if (useMockData) {
      return mockResponse([]);
    }
    const response = await api.get(`/v1/user/responses/by-question/${questionId}/`);
    return { data: response };
  },
  save: async (data) => {
    if (useMockData) {
      return mockResponse({ success: true, data });
    }
    // Save single response
    const response = await api.post('/v1/user/responses/', data);
    return { data: response };
  },
  bulkSave: async (responses) => {
    if (useMockData) {
      return mockResponse({ created: responses.length, responses });
    }
    // Bulk save multiple responses (for localStorage sync)
    const response = await api.post('/v1/user/responses/bulk_save/', { responses });
    return { data: response };
  },
  getSummary: async () => {
    if (useMockData) {
      return mockResponse({ responses: [], summary: {} });
    }
    const response = await api.get('/v1/user/responses/summary/');
    return { data: response };
  },
  /**
   * Get team members who picked the same choices as a user
   * @param {string} teamId - Team UUID
   * @param {string} questionId - Question UUID
   * @param {string} memberId - Optional. If provided, find matches for this member instead of current user
   * @returns {Promise} Response with layers data showing matching members per layer
   */
  getTeamChoices: async (teamId, questionId, memberId = null) => {
    if (useMockData) {
      return mockResponse({ layers: [] });
    }
    let url = `/v1/user/responses/team-choices/${teamId}/${questionId}/`;
    if (memberId) {
      url += `?member_id=${memberId}`;
    }
    const response = await api.get(url);
    return { data: response };
  },
  /**
   * Get a team member's responses for a specific question
   * @param {string} memberId - Member's user UUID
   * @param {string} questionId - Question UUID
   * @returns {Promise} Response with reflections data for the member
   */
  getMemberResponses: async (memberId, questionId) => {
    if (useMockData) {
      return mockResponse({ reflections: [], member: null });
    }
    const response = await api.get(`/v1/user/responses/member/${memberId}/question/${questionId}/`);
    return { data: response };
  },
  /**
   * Export a PDF of the Advance Directive Helper for a user's responses
   * @param {string} questionId - Question UUID
   * @param {string} memberId - Optional. Member's user UUID (defaults to current user)
   * @param {string} teamId - Optional. Team UUID (for fetching designations and affirmations)
   * @param {boolean} preview - Optional. If true, PDF opens in browser tab instead of downloading
   * @returns {Promise} Response with PDF blob data
   */
  exportPDF: async (questionId, memberId, teamId, preview = false) => {
    if (useMockData) {
      const mockBlob = new Blob(['Mock PDF data'], { type: 'application/pdf' });
      return mockResponse(mockBlob);
    }

    try {
      const response = await api.post('/v1/user/responses/export-pdf/', {
        question_id: questionId,
        member_id: memberId,
        team_id: teamId,
        preview: preview
      }, {
        responseType: 'blob'  // Critical: receive binary data instead of JSON
      });
      // Note: api interceptor already returns response.data, so 'response' is the blob
      return response;
    } catch (error) {
      // Handle blob error responses - convert blob to text to get error message
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          const errorMsg = json.error || json.detail || 'Failed to export PDF';
          throw new Error(errorMsg);
        } catch (e) {
          throw new Error(text || 'Failed to export PDF');
        }
      }
      throw error;
    }
  },

  /**
   * Export a PDF of the Advance Directive Helper for ALL questions
   * @param {string} teamId - Team UUID (for fetching designations and affirmations)
   * @param {boolean} preview - Optional. If true, PDF opens in browser tab instead of downloading
   * @returns {Promise} Response with PDF blob data
   */
  exportFullPDF: async (teamId, preview = false) => {
    if (useMockData) {
      const mockBlob = new Blob(['Mock PDF data'], { type: 'application/pdf' });
      return mockResponse(mockBlob);
    }

    try {
      const response = await api.post('/v1/user/responses/export-full-pdf/', {
        team_id: teamId,
        preview: preview
      }, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          const errorMsg = json.error || json.detail || 'Failed to export PDF';
          throw new Error(errorMsg);
        } catch (e) {
          throw new Error(text || 'Failed to export PDF');
        }
      }
      throw error;
    }
  },

  exportTeamPDF: async (teamId) => {
    if (useMockData) {
      const mockBlob = new Blob(['Mock PDF data'], { type: 'application/pdf' });
      return mockResponse(mockBlob);
    }

    try {
      const response = await api.post('/v1/user/responses/export-team-pdf/', {
        team_id: teamId,
      }, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          const errorMsg = json.error || json.detail || 'Failed to export team PDF';
          throw new Error(errorMsg);
        } catch (e) {
          throw new Error(text || 'Failed to export team PDF');
        }
      }
      throw error;
    }
  }
};

// Progress service - manages user questionnaire progress
export const progressService = {
  getAll: async (params = {}) => {
    if (useMockData) {
      return mockResponse([]);
    }
    const response = await api.get('/v1/user/progress/', { params });
    return { data: response };
  },
  getByQuestion: async (questionId) => {
    if (useMockData) {
      return mockResponse(null);
    }
    const response = await api.get(`/v1/user/progress/by-question/${questionId}/`);
    return { data: response };
  },
  save: async (data) => {
    if (useMockData) {
      return mockResponse({ success: true, data });
    }
    // Save progress (creates or updates)
    const response = await api.post('/v1/user/progress/', data);
    return { data: response };
  },
  getTimeSummary: async () => {
    if (useMockData) {
      return mockResponse({ total_time_spent_seconds: 0 });
    }
    const response = await api.get('/v1/user/progress/time-summary/');
    return { data: response };
  }
};

// Team service - fetches team member data
export const teamService = {
  getAll: async () => {
    if (useMockData) {
      return mockResponse(mockData.team);
    }
    // TODO: Implement team endpoint in Django backend
    return mockResponse(mockData.team);
  }
};

// Users service - kept for backward compatibility
export const usersService = {
  getTeam: async () => {
    if (useMockData) {
      return mockResponse(mockData.team);
    }
    // TODO: Implement team endpoint in Django backend
    return mockResponse(mockData.team);
  },
  getSections: async () => {
    if (useMockData) {
      return mockResponse(mockData.sections);
    }
    // TODO: Implement sections endpoint in Django backend
    return mockResponse(mockData.sections);
  }
};

// Health check service
export const healthService = {
  check: async () => {
    if (useMockData) {
      return mockResponse({ status: 'ok' });
    }
    // Django doesn't have health endpoint by default, just check API root
    const response = await api.get('/v1/content/questions/');
    return { data: { status: 'ok' } };
  }
};
